import workspaceService from "api/workspaceService";
import WorkspaceMembers from "components/WorkspaceMembers";
import AddWorker from "components/Settings/AddWorker";
import { IWorkspace } from "dto/IWorkspace";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPrivilages } from "utils/getPrivilages";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import styles from "./styles.module.scss";
import TeamIcon from "assets/icons/team.svg?react";
import Spinner from "components/Spinner";
import { useAuth } from "react-oidc-context";
import { Claims } from "dto/User/claims";
import { getUserClaim } from "utils/getUserClaim";
import { EUserType } from "dto/User/EUserType";
import StructureEditor from "./StructureEditor";
import KadroweTab from "../DictionariesTab/KadroweTab";
import CreateWorkspace from "components/CreateWorkspace";

// Zakładka „Zespół". Jedno źródło prawdy o osobach: lista członków zespołu z BE
// (WorkspaceMembers). Struktura organizacyjna (WorkspaceStructureItems) przypisuje
// TYCH SAMYCH członków do komórek — nie ma osobnej, sztucznej listy pracowników.
// Uprawnienia osoby ustawia się w jej metryce (Lista osób → „Edytuj" w menu członka) i roli
// firmowej — dawna podzakładka „Uprawnienia" (blob team_privileges_v1) została usunięta.

type SubTab = "members" | "structure" | "kadry";

const WorkersTab = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const isLegalPerson =
    getUserClaim(auth.user, Claims.UserTypeClaim) === EUserType.LegalPerson;

  const [subTab, setSubTab] = useState<SubTab>("members");
  const [showAdd, setShowAdd] = useState(false);

  // D1 (model-kluczy-firmowych): jedna firma na użytkownika, bez przełącznika kontekstu —
  // workspaceId czytamy wprost z mirrora firmy. `workspaceIdOverride` to awaryjny nadpis
  // na czas między utworzeniem zespołu (CreateWorkspace) a odświeżeniem mirrora.
  const [workspaceIdOverride, setWorkspaceIdOverride] = useState<string | null>(
    null,
  );
  const workspaceId = workspaceIdOverride ?? getWorkspaceId() ?? "";
  const {
    data: workspaceData,
    refetch,
    isLoading: isLoadingWorkspace,
  } = workspaceService.useGetWorkspace(workspaceId);

  const workspace: IWorkspace = useMemo(
    () => (workspaceData?.data as IWorkspace) ?? null,
    [workspaceData],
  );

  const workspaceMembers = useMemo(() => workspace?.Members ?? [], [workspace]);
  const workspaceRoles = useMemo(() => workspace?.Roles ?? [], [workspace]);

  const me = workspaceMembers.find((membership) => membership.IsMe === true);
  const meRole = workspaceRoles.find((role) => role.Id === me?.RoleId);
  const myPrivilages = getPrivilages(meRole);

  const canInvite = !!me?.IsOwner || myPrivilages.CanInviteMembersToWorkspace;
  const canEditMembers = !!me?.IsOwner || myPrivilages.CanEditMembersInWorkspace;
  const canEditStructure = !!me?.IsOwner || myPrivilages.CanEditWorkspaceStructure;

  if (!workspaceId) {
    return <CreateWorkspace onCreate={setWorkspaceIdOverride} />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        <TeamIcon />
        {t("roles.team")}
      </div>

      <div className={styles.subTabs}>
        <div
          className={`${styles.subTab} ${subTab === "members" ? styles.subTabActive : ""}`}
          onClick={() => setSubTab("members")}
        >
          Lista osób
        </div>
        <div
          className={`${styles.subTab} ${subTab === "structure" ? styles.subTabActive : ""}`}
          onClick={() => setSubTab("structure")}
        >
          Struktura
        </div>
        <div
          className={`${styles.subTab} ${subTab === "kadry" ? styles.subTabActive : ""}`}
          onClick={() => setSubTab("kadry")}
        >
          Stopnie/Stanowiska
        </div>
      </div>

      {subTab === "members" && (
        <div className={styles.membersContent}>
          {isLoadingWorkspace && <Spinner />}
          {!isLoadingWorkspace && workspace && (
            <>
              {canInvite && (
                <div className={styles.membersAddRow}>
                  <button
                    className={styles.addPersonBtn}
                    onClick={() => setShowAdd(true)}
                  >
                    + Dodaj członka zespołu
                  </button>
                </div>
              )}
              {/* showAdd={false}: AddWorker renderujemy tu (niżej), nie w WorkspaceMembers */}
              <WorkspaceMembers
                workspace={workspace}
                showAdd={false}
                setShowAdd={(val) => val && setShowAdd(true)}
                refetch={refetch}
                myPrivilages={myPrivilages}
                roles={workspaceRoles}
                members={workspaceMembers.filter(
                  (x) => !x.IsMe || !isLegalPerson,
                )}
              />
            </>
          )}

          {showAdd && workspace && (
            <AddWorker
              roles={workspaceRoles}
              structure={workspace.Structure ?? []}
              toggle={() => setShowAdd(false)}
              refetch={refetch}
              workspaceId={workspace.Id}
            />
          )}
        </div>
      )}

      {subTab === "structure" && (
        <div className={styles.orgChartWrapper}>
          {isLoadingWorkspace || !workspace ? (
            <Spinner />
          ) : (
            <StructureEditor
              workspace={workspace}
              refetch={refetch}
              canEditStructure={canEditStructure}
              canEditMembers={canEditMembers}
            />
          )}
        </div>
      )}

      {subTab === "kadry" && (
        <div className={styles.orgChartWrapper}>
          <KadroweTab />
        </div>
      )}
    </div>
  );
};

export default WorkersTab;
